import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, Plus, Users, Gift, Clock, Mail, Sparkles, Copy, Check, Loader2, Cake, Eye } from "lucide-react";
import { useTranslation } from "react-i18next";
import { usePlayer } from "@/context/PlayerContext";
import {
  getFamily, addChild, generateGiftCode, listGiftCodes,
  updateScreenTime, sendWeeklyReport, redeemGiftCode,
  previewWeeklyReport, scheduleGift, listScheduledGifts, sendScheduledGift,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import SoundToggle from "@/components/SoundToggle";

export default function Family() {
  const { t } = useTranslation();
  const { player, setPlayer, familyId, switchToChild } = usePlayer();
  const navigate = useNavigate();
  const [family, setFamily] = useState(null);
  const [children, setChildren] = useState([]);
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [newMode, setNewMode] = useState("discovery");
  const [emailOverride, setEmailOverride] = useState("");
  const [redeem, setRedeem] = useState("");
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(null);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [scheduled, setScheduled] = useState([]);
  const [giftChildId, setGiftChildId] = useState("");
  const [giftDate, setGiftDate] = useState("");
  const [giftMessage, setGiftMessage] = useState("");
  const [giftTier, setGiftTier] = useState("premium");

  const load = async () => {
    if (!familyId) return;
    try {
      const res = await getFamily(familyId);
      setFamily(res.family);
      setChildren(res.children);
      const c = await listGiftCodes(familyId);
      setCodes(c);
      const sg = await listScheduledGifts(familyId);
      setScheduled(sg);
    } catch (e) {
      toast.error(t("family.load_error"));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [familyId]);  // eslint-disable-line

  const handleAddChild = async () => {
    if (!newName.trim()) { toast.error(t("family.missing_name")); return; }
    if (children.length >= 4) { toast.error(t("family.max_children")); return; }
    try {
      await addChild(familyId, { name: newName, mode: newMode });
      setNewName("");
      toast.success(t("family.child_added_ok"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("family.generic_error"));
    }
  };

  const handleGenerateCode = async (tier) => {
    try {
      const r = await generateGiftCode(familyId, tier);
      toast.success(t("family.code_created", { tier, code: r.code }));
      load();
    } catch (e) { toast.error(t("family.code_gen_error")); }
  };

  const handleCopy = (code) => {
    navigator.clipboard?.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(null), 1500);
  };

  const handleScreenTime = async (childId, minutes, enabled) => {
    try {
      await updateScreenTime(childId, { daily_minutes: minutes, enabled });
      toast.success(t("family.screen_time_updated"));
      load();
    } catch (e) { toast.error(t("family.generic_error")); }
  };

  const handleSendReport = async () => {
    setSending(true);
    try {
      const r = await sendWeeklyReport(familyId, emailOverride || undefined);
      toast.success(t("family.sent_ok", { email: r.recipient }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("family.send_error"));
    } finally { setSending(false); }
  };

  const handleRedeem = async () => {
    if (!redeem.trim() || !player?.id) return;
    try {
      const r = await redeemGiftCode(redeem.trim(), player.id);
      setPlayer({ ...player, premium_tier: r.tier });
      toast.success(t("family.code_activated", { tier: r.tier }));
      setRedeem("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("family.code_invalid")); }
  };

  const handleSwitch = async (childId) => {
    const p = await switchToChild(childId);
    if (p) { toast.success(t("family.welcome_child", { name: p.name })); navigate("/levels"); }
  };

  const handlePreview = async () => {
    try {
      const r = await previewWeeklyReport(familyId);
      setPreviewHtml(r.html);
    } catch (e) { toast.error(t("family.preview_error")); }
  };

  const handleScheduleGift = async () => {
    if (!giftChildId || !giftDate) { toast.error(t("family.gift_missing")); return; }
    try {
      await scheduleGift({
        family_id: familyId, child_id: giftChildId,
        delivery_date: giftDate, message: giftMessage, tier: giftTier,
      });
      toast.success(t("family.gift_scheduled_ok"));
      setGiftChildId(""); setGiftDate(""); setGiftMessage("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("family.generic_error")); }
  };

  const handleSendScheduled = async (gid) => {
    try {
      await sendScheduledGift(gid);
      toast.success(t("family.gift_sent_ok"));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("family.generic_error")); }
  };

  if (!familyId) {
    return (
      <div className="min-h-screen hero-radial grain flex items-center justify-center p-6">
        <div className="glass-strong rounded-3xl p-8 max-w-md text-center">
          <Users className="w-10 h-10 text-amber-300 mx-auto mb-3" />
          <h2 className="font-serif text-2xl font-bold">{t("family.no_family_title")}</h2>
          <p className="text-stone-300 mt-2">{t("family.no_family_text")}</p>
          <Link to="/profile" className="inline-block mt-4 text-amber-300 hover:text-amber-200 font-bold" data-testid="family-go-profile">
            {t("family.no_family_cta")}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen hero-radial grain">
      <header className="relative z-30 flex items-center justify-between px-6 sm:px-10 py-5">
        <Link to="/levels" className="flex items-center gap-2 text-stone-300 hover:text-amber-200" data-testid="back-levels">
          <ChevronLeft className="w-4 h-4" /> {t("common.back_levels")}
        </Link>
        <div className="flex items-center gap-2 font-serif text-lg">
          <Users className="w-5 h-5 text-amber-300" /> {t("family.header")}
        </div>
        <SoundToggle />
        <LanguageSwitcher />
      </header>

      <main className="relative z-10 max-w-5xl mx-auto px-6 sm:px-10 pb-20 space-y-6">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-amber-300/90 font-bold">{t("family.kicker")}</div>
          <h2 className="mt-1 font-serif text-4xl sm:text-5xl font-black text-stone-50">
            {t("family.title_1")}<span className="italic text-amber-300">{t("family.title_2")}</span>
          </h2>
        </div>

        {loading ? (
          <div className="glass rounded-2xl p-6 flex items-center gap-3 text-stone-400">
            <Loader2 className="w-4 h-4 animate-spin" /> {t("family.loading")}
          </div>
        ) : (
          <>
            <section className="glass rounded-3xl p-6" data-testid="family-children">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-serif text-2xl font-bold text-stone-50">{t("family.children_title", { n: children.length })}</h3>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {children.map((c) => (
                  <div key={c.id} className={`rounded-2xl border p-4 ${c.id === player?.id ? "border-amber-400 bg-amber-500/10" : "border-white/10 bg-black/30"}`} data-testid={`child-card-${c.id}`}>
                    <div className="flex items-center gap-3">
                      {c.custom_portrait ? (
                        <img src={`data:image/png;base64,${c.custom_portrait}`} alt="" className="w-12 h-12 rounded-full object-cover border border-amber-400/50" />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-orange-700 flex items-center justify-center font-serif text-lg font-black text-stone-950">
                          {c.name?.[0]?.toUpperCase() || "L"}
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="font-serif text-lg font-bold text-stone-50">{c.name}</div>
                        <div className="text-xs text-stone-400">{t("family.child_puzzles_prefix", { mode: c.mode, n: c.puzzles_solved || 0 })}</div>
                      </div>
                    </div>

                    <div className="mt-3 border-t border-white/10 pt-3">
                      <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-stone-300 mb-2">
                        <Clock className="w-3.5 h-3.5" /> {t("family.screen_time")}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {[15, 30, 45, 60, 90].map((m) => (
                          <button
                            key={m}
                            onClick={() => handleScreenTime(c.id, m, true)}
                            data-testid={`screen-time-${c.id}-${m}`}
                            className={`px-3 py-1 rounded-full text-xs font-bold border ${
                              c.screen_time_enabled && c.screen_time_daily_minutes === m
                                ? "bg-amber-400 text-stone-950 border-amber-400"
                                : "bg-black/40 text-stone-300 border-white/15 hover:border-amber-400/50"
                            }`}
                          >{m} min</button>
                        ))}
                        <button
                          onClick={() => handleScreenTime(c.id, c.screen_time_daily_minutes || 60, false)}
                          className="px-3 py-1 rounded-full text-xs font-bold text-stone-400 hover:text-stone-200"
                          data-testid={`screen-time-${c.id}-off`}
                        >{t("family.unlimited")}</button>
                      </div>
                    </div>

                    {c.id !== player?.id && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSwitch(c.id)}
                        className="mt-3 w-full border-white/20 bg-transparent text-stone-200 hover:bg-white/5"
                        data-testid={`switch-child-${c.id}`}
                      >
                        {t("family.switch_to")}
                      </Button>
                    )}
                  </div>
                ))}

                {children.length < 4 && (
                  <div className="rounded-2xl border-2 border-dashed border-white/15 p-4" data-testid="add-child-form">
                    <div className="flex items-center gap-2 mb-2">
                      <Plus className="w-4 h-4 text-emerald-300" />
                      <span className="font-bold text-sm text-stone-200">{t("family.add_child")}</span>
                    </div>
                    <Input
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      placeholder={t("family.child_name")}
                      className="bg-black/40 border-white/15 mb-2"
                      data-testid="input-new-child-name"
                    />
                    <div className="flex gap-2 mb-3">
                      {[{k:"discovery", l:t("profile.mode_discovery")}, {k:"engineer", l:t("profile.mode_engineer")}].map(m => (
                        <button
                          key={m.k}
                          onClick={() => setNewMode(m.k)}
                          data-testid={`new-child-mode-${m.k}`}
                          className={`flex-1 px-2 py-1 rounded-lg text-xs font-bold ${newMode === m.k ? "bg-amber-400 text-stone-950" : "bg-black/40 text-stone-300 border border-white/15"}`}
                        >{m.l}</button>
                      ))}
                    </div>
                    <Button
                      onClick={handleAddChild}
                      size="sm"
                      className="w-full bg-emerald-500 hover:bg-emerald-400 text-stone-950 font-bold"
                      data-testid="btn-add-child"
                    >{t("common.add")}</Button>
                  </div>
                )}
              </div>
            </section>

            <section className="glass rounded-3xl p-6" data-testid="family-gift-codes">
              <div className="flex items-center gap-2 mb-4">
                <Gift className="w-5 h-5 text-fuchsia-300" />
                <h3 className="font-serif text-2xl font-bold text-stone-50">{t("family.gift_codes_title")}</h3>
              </div>
              <p className="text-sm text-stone-400 mb-4">{t("family.gift_codes_desc")}</p>

              <div className="flex flex-wrap gap-2 mb-5">
                <Button onClick={() => handleGenerateCode("premium")} size="sm" className="bg-amber-400 hover:bg-amber-300 text-stone-950 font-bold" data-testid="gen-code-premium">
                  <Sparkles className="w-3.5 h-3.5 mr-1" /> {t("family.gen_premium")}
                </Button>
                <Button onClick={() => handleGenerateCode("family")} size="sm" className="bg-emerald-500 hover:bg-emerald-400 text-stone-950 font-bold" data-testid="gen-code-family">
                  <Sparkles className="w-3.5 h-3.5 mr-1" /> {t("family.gen_family")}
                </Button>
              </div>

              <div className="space-y-2" data-testid="gift-codes-list">
                {codes.length === 0 && <div className="text-xs text-stone-500 italic">{t("family.empty_codes")}</div>}
                {codes.map((c) => (
                  <div key={c.code} className="flex items-center justify-between rounded-xl bg-black/40 border border-white/10 px-3 py-2">
                    <div>
                      <span className="font-mono text-sm text-amber-200">{c.code}</span>
                      <span className="ml-3 text-xs uppercase tracking-widest text-stone-400">{c.tier}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {c.redeemed_by ? (
                        <span className="text-xs text-emerald-300">{t("family.used")}</span>
                      ) : (
                        <button onClick={() => handleCopy(c.code)} className="text-stone-400 hover:text-stone-100" data-testid={`copy-${c.code}`}>
                          {copied === c.code ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 border-t border-white/10 pt-4">
                <div className="text-xs uppercase tracking-widest font-bold text-stone-300 mb-2">{t("family.redeem_title", { name: player?.name })}</div>
                <div className="flex gap-2">
                  <Input
                    value={redeem}
                    onChange={(e) => setRedeem(e.target.value)}
                    placeholder={t("family.code_placeholder")}
                    className="bg-black/40 border-white/15 font-mono uppercase"
                    data-testid="input-redeem-code"
                  />
                  <Button onClick={handleRedeem} className="bg-fuchsia-500 hover:bg-fuchsia-400 text-stone-950 font-bold" data-testid="btn-redeem-code">
                    {t("common.validate")}
                  </Button>
                </div>
              </div>
            </section>

            <section className="glass rounded-3xl p-6" data-testid="family-weekly-report">
              <div className="flex items-center gap-2 mb-4">
                <Mail className="w-5 h-5 text-teal-300" />
                <h3 className="font-serif text-2xl font-bold text-stone-50">{t("family.weekly_title")}</h3>
              </div>
              <p className="text-sm text-stone-400 mb-4">{t("family.weekly_desc")} <span className="text-teal-300">{family?.parent_email || "—"}</span></p>

              <div className="flex flex-col sm:flex-row gap-3">
                <Input
                  value={emailOverride}
                  onChange={(e) => setEmailOverride(e.target.value)}
                  placeholder={family?.parent_email || t("family.email_placeholder")}
                  type="email"
                  className="bg-black/40 border-white/15 flex-1"
                  data-testid="input-weekly-email"
                />
                <Button
                  onClick={handlePreview}
                  variant="outline"
                  className="border-teal-400/50 bg-transparent text-teal-200 hover:bg-teal-900/20 rounded-full px-4"
                  data-testid="btn-preview-report"
                >
                  <Eye className="w-4 h-4 mr-2" /> {t("common.preview")}
                </Button>
                <Button
                  onClick={handleSendReport}
                  disabled={sending}
                  className="bg-teal-500 hover:bg-teal-400 text-stone-950 font-bold rounded-full px-6"
                  data-testid="btn-send-weekly-report"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                  {sending ? t("family.sending") : t("family.send_now")}
                </Button>
              </div>
            </section>

            <section className="glass rounded-3xl p-6" data-testid="family-scheduled-gifts">
              <div className="flex items-center gap-2 mb-4">
                <Cake className="w-5 h-5 text-rose-300" />
                <h3 className="font-serif text-2xl font-bold text-stone-50">{t("family.scheduled_title")}</h3>
              </div>
              <p className="text-sm text-stone-400 mb-4">{t("family.scheduled_desc")}</p>

              <div className="grid sm:grid-cols-2 gap-3 mb-4">
                <div>
                  <label className="block text-xs uppercase tracking-widest font-bold text-stone-300 mb-1">{t("family.gift_child")}</label>
                  <select value={giftChildId} onChange={(e) => setGiftChildId(e.target.value)} data-testid="select-gift-child" className="w-full bg-black/40 border border-white/15 rounded-md h-10 px-3 text-stone-100">
                    <option value="">{t("family.gift_pick")}</option>
                    {children.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest font-bold text-stone-300 mb-1">{t("family.gift_date")}</label>
                  <Input type="date" value={giftDate} onChange={(e) => setGiftDate(e.target.value)} className="bg-black/40 border-white/15" data-testid="input-gift-date" />
                </div>
              </div>
              <Input value={giftMessage} onChange={(e) => setGiftMessage(e.target.value)} placeholder={t("family.gift_message_placeholder")} maxLength={280} className="bg-black/40 border-white/15 mb-3" data-testid="input-gift-message" />
              <div className="flex flex-wrap gap-2 mb-3">
                {["premium", "family", "supporter"].map((tt) => (
                  <button key={tt} onClick={() => setGiftTier(tt)}
                    data-testid={`gift-tier-${tt}`}
                    className={`px-3 py-1 rounded-full text-xs font-bold ${giftTier === tt ? "bg-rose-400 text-stone-950" : "bg-black/40 border border-white/15 text-stone-300"}`}
                  >{tt}</button>
                ))}
              </div>
              <Button onClick={handleScheduleGift} className="w-full bg-rose-500 hover:bg-rose-400 text-stone-950 font-bold" data-testid="btn-schedule-gift">
                <Cake className="w-4 h-4 mr-2" /> {t("family.gift_schedule")}
              </Button>

              {scheduled.length > 0 && (
                <div className="mt-5 space-y-2" data-testid="scheduled-gifts-list">
                  {scheduled.map((g) => {
                    const today = new Date().toISOString().slice(0, 10);
                    const due = g.delivery_date <= today && g.status !== "sent";
                    return (
                      <div key={g.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-black/40 border border-white/10 px-3 py-2">
                        <div>
                          <div className="text-sm text-stone-100">
                            🎂 {g.child_name} · <span className="font-mono text-rose-200">{g.code}</span>
                          </div>
                          <div className="text-xs text-stone-400">{t("family.on_date", { date: g.delivery_date, tier: g.tier, status: g.status })}</div>
                        </div>
                        {due ? (
                          <Button size="sm" onClick={() => handleSendScheduled(g.id)} className="bg-rose-500 hover:bg-rose-400 text-stone-950 font-bold" data-testid={`btn-send-scheduled-${g.id}`}>
                            <Mail className="w-3 h-3 mr-1" /> {t("family.gift_send")}
                          </Button>
                        ) : g.status === "sent" ? (
                          <span className="text-xs text-emerald-300">{t("family.sent")}</span>
                        ) : (
                          <span className="text-xs text-stone-500">{t("family.coming")}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </>
        )}
      </main>

      {previewHtml && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur" onClick={() => setPreviewHtml(null)} data-testid="preview-modal">
          <div onClick={(e) => e.stopPropagation()} className="glass-strong rounded-3xl p-4 max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-2 px-2">
              <div className="text-xs uppercase tracking-[0.3em] text-teal-300 font-bold">{t("family.preview_kicker")}</div>
              <button onClick={() => setPreviewHtml(null)} className="text-stone-400 hover:text-stone-100" data-testid="btn-close-preview">✕</button>
            </div>
            <iframe title={t("family.preview_title")} srcDoc={previewHtml} className="w-full flex-1 rounded-2xl bg-white" style={{ minHeight: "500px" }} data-testid="preview-iframe" />
          </div>
        </div>
      )}
    </div>
  );
}
